/**
 * Refresh-in-flight bookkeeping for `HelmPage` (A7Y-3870).
 *
 * `run.cjs start` only writes the `helm_runs/{runId}` doc after the GitHub
 * Actions job has run checkout, `npm ci` and the Claude CLI install — several
 * minutes after `POST /helm/refresh` accepts the click. Until that doc
 * exists, `overview.inFlight` is `null`: that is NOT evidence the refresh
 * finished, only that its run doc hasn't landed yet. So `HelmPage` must not
 * clear its local `requestedAt` on "no inFlight" alone — only once a run
 * that actually started at-or-after the click is observed, either still
 * running (`inFlight`) or already resolved (`latestAttempt`/`current`).
 */
import type { HelmOverviewResponse } from '@a7y/platform-health/helm';

/** Generous bound over checkout + `npm ci` + CLI install + collect + compose
 * + publish, so a genuinely lost run doesn't show "Refreshing" forever. */
export const HELM_REFRESH_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * True once a run that supersedes the click (`startedAt >= requestedAt`) is
 * either currently running or has already resolved (succeeded, failed, or
 * abandoned) — i.e. the click has definitely been served, one way or
 * another. False while nothing newer than the click has been observed yet,
 * even though `inFlight` is still `null` during the pre-doc setup window.
 */
export function isHelmRefreshServed(
  overview: HelmOverviewResponse,
  requestedAtIso: string
): boolean {
  const requestedAt = Date.parse(requestedAtIso);
  if (Number.isNaN(requestedAt)) return false;

  const attempt = overview.latestAttempt;
  if (attempt) {
    const attemptStartedAt = Date.parse(attempt.startedAt);
    if (
      !Number.isNaN(attemptStartedAt) &&
      attemptStartedAt >= requestedAt &&
      attempt.status !== 'running'
    ) {
      return true;
    }
  }

  const current = overview.current;
  if (current) {
    const currentStartedAt = Date.parse(current.summary.startedAt);
    if (!Number.isNaN(currentStartedAt) && currentStartedAt >= requestedAt) {
      return true;
    }
  }

  return false;
}
